<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElButton, ElButtonGroup, ElInput, ElOption, ElSelect, ElTable, ElTableColumn} from 'element-plus';
import {defaultStatusLabels, filterKpiRows, filterKpiRowsForTotals, formatWaarde, KpiRow, KpiRowFilter, KpisPage, KpiStatusTotal, KpiTypeFilter, ToegekendStatusEnum, totalsByStatus, WaardeTypeEnum} from 'common';
import {dashboardKpis} from '@/base/callBackend.js';
import {dashboardOrgs, dashboardPeriod, laadDashboard} from '@/base/dashboardState.js';
import KpiCard from '@/views/dashboard/KpiCard.vue';

const TYPE_OPTIONS: KpiTypeFilter[] = ['KPI', 'Mijlpaal', 'Alle'];
const WARNING_ACCENT: string        = '#F7E4AB';
const DEFAULT_ACCENT: string        = '#f7f7f7';

const loading   = ref<boolean>(true);
const rows      = ref<KpiRow[]>([]);
const filter    = ref<KpiRowFilter>({type: 'KPI', statuses: [], statusLabels: [], kwartalen: [], zoek: ''});
const kwartalen = computed<string[]>(() => [...new Set(rows.value.map(r => r.kwartaal))].sort().reverse());
const labels    = computed<string[]>(() => [...new Set(rows.value.map(r => r.statusLabel))].sort());
const totals    = computed<KpiStatusTotal[]>(() => totalsByStatus(filterKpiRowsForTotals(rows.value, filter.value)));
const shownRows = computed<KpiRow[]>(() => filterKpiRows(rows.value, filter.value));

let loadSeq: number = 0;

watch([dashboardPeriod, dashboardOrgs], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number               = ++loadSeq;
    loading.value                   = true;
    const page: KpisPage | undefined = await laadDashboard(() => dashboardKpis(dashboardPeriod.value, dashboardOrgs.value));
    if (seq !== loadSeq) {
        return;
    }
    rows.value                = page?.rows ?? [];
    filter.value.statusLabels = defaultStatusLabels(rows.value);
    filter.value.kwartalen    = filter.value.kwartalen.filter(q => kwartalen.value.includes(q));
    loading.value             = false;
}

// a card toggles its status as the only status filter
function toggleStatus(status: ToegekendStatusEnum) {
    filter.value.statuses = filter.value.statuses.includes(status) ? [] : [status];
}

function accentOf(status: ToegekendStatusEnum): string {
    return status === ToegekendStatusEnum.verwachtNietExtraToegekend ? WARNING_ACCENT : DEFAULT_ACCENT;
}

function bedrag(v: number): string {
    return formatWaarde(v, WaardeTypeEnum.bedrag);
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-4">
        <div class="flex flex-row flex-wrap gap-4">
            <KpiCard v-for="t in totals"
                     :key="t.status"
                     data-testid="kpis-status-card"
                     :label="t.status"
                     :value="bedrag(t.bedrag)"
                     :detail="`${t.aantalKpis} KPI's, ${t.aantalMijlpalen} mijlpalen`"
                     :active="filter.statuses.includes(t.status)"
                     :accent="accentOf(t.status)"
                     @click="toggleStatus(t.status)"
            />
        </div>

        <div class="flex flex-row flex-wrap items-center gap-4">
            <el-button-group>
                <el-button v-for="t in TYPE_OPTIONS"
                           :key="t"
                           :type="filter.type === t ? 'primary' : 'default'"
                           @click="filter.type = t"
                >
                    {{ t }}
                </el-button>
            </el-button-group>
            <el-select v-model="filter.statusLabels" multiple collapse-tags placeholder="Status" class="w-56">
                <el-option v-for="l in labels" :key="l" :value="l" :label="l"/>
            </el-select>
            <el-select v-model="filter.kwartalen" multiple collapse-tags placeholder="Kwartaal" class="w-56">
                <el-option v-for="q in kwartalen" :key="q" :value="q" :label="q"/>
            </el-select>
            <el-input v-model="filter.zoek" placeholder="Zoeken" clearable class="w-64"/>
            <span class="text-sm text-gray-500">{{ loading ? 'laden...' : `${shownRows.length} regels` }}</span>
        </div>

        <el-table :data="shownRows" stripe size="small" max-height="700">
            <el-table-column prop="vecozoId" label="ID" width="120"/>
            <el-table-column prop="naam" label="KPI / mijlpaal" min-width="260"/>
            <el-table-column prop="thema" label="Thema" width="180"/>
            <el-table-column prop="organisatie" label="Organisatie" width="160"/>
            <el-table-column prop="kwartaal" label="Kwartaal" width="100"/>
            <el-table-column prop="toegekendStatus" label="Toegekend status" width="220"/>
            <el-table-column label="Bedrag" width="130" align="right">
                <template #default="scope">{{ bedrag(scope.row.bedrag) }}</template>
            </el-table-column>
            <el-table-column prop="toelichting" label="Toelichting" min-width="300" show-overflow-tooltip/>
        </el-table>
    </div>
</template>
